<script lang="ts">
	import { onMount } from 'svelte';
	import { locale as localeStore, t } from 'svelte-intl-precompile';
	import { page } from '$app/stores';
	import Select from '$lib/components/atoms/Select/Select.svelte';
	import Switch from '$lib/components/atoms/Switch/Switch.svelte';
	import LinkedinIcon from '$lib/assets/svg/icons/linkedin.svg?component';
	import TwitterIcon from '$lib/assets/svg/icons/twitter-x.svg?component';
	import YoutubeIcon from '$lib/assets/svg/icons/youtube.svg?component';
	import InstagramIcon from '$lib/assets/svg/icons/instagram.svg?component';
	import {
		getValidLocale,
		countryCodeLookup,
		getTextByCountry,
		getTextByCountryLang
	} from '$lib/utils';
	import {
		LANGUAGES,
		PRIVACY_POLICIES,
		CONTACTS_BY_COUNTRY,
		LEGAL_ENTITIES_BY_COUNTRY,
		PUBLISHING_DETAILS_BY_COUNTRY,
		OFFICE_ADDRESS_BY_COUNTRY,
		OFFICE_PHONE_NUMBER_BY_COUNTRY,
		PAGES
	} from '$lib/consts';

	interface Props {
		centered?: boolean;
	}

	let { centered = false }: Props = $props();

	let socialIcons = [
		{
			icon: LinkedinIcon,
			href: 'https://linkedin.com/company/swiss-life-asset-management',
			label: 'LinkedIn'
		},
		{
			icon: TwitterIcon,
			href: 'https://x.com/SwissLife_AM',
			label: 'Twitter'
		},
		{
			icon: YoutubeIcon,
			href: 'https://www.youtube.com/@swisslifeassetmanagers',
			label: 'Youtube'
		},
		{
			icon: InstagramIcon,
			href: 'https://www.instagram.com/swisslife',
			label: 'Instagram'
		}
	];

	let currentTheme: 'light' | 'dark' = $state('light');

	const langItems = LANGUAGES.map((lang) => ({
		value: lang,
		label: $t(`language.${lang}`)
	}));

	let country = $derived($page.data.invCountryCookie ?? 'Default');
	let lang = $derived($localeStore.substring(0, 2));
	let invType = $derived($page.data.invTypeCookie ?? 'Default');

	onMount(() => {
		setTheme((document.documentElement.dataset.theme as 'light' | 'dark') ?? 'light');
	});

	const setTheme = (theme: 'light' | 'dark', save = false) => {
		document.documentElement.dataset.theme = theme;
		currentTheme = theme;
		if (save) document.cookie = `theme=${theme};max-age=31536000;path="/"`;
	};

	let contactsHref = $derived(getTextByCountryLang(CONTACTS_BY_COUNTRY, country, lang));
	let address = $derived(getTextByCountryLang(OFFICE_ADDRESS_BY_COUNTRY, country, lang));
	let phone = $derived(getTextByCountry(OFFICE_PHONE_NUMBER_BY_COUNTRY, country));
	let dataProtectionHref = $derived(getTextByCountryLang(PRIVACY_POLICIES, country, lang));
	let detailsHref = $derived(getTextByCountryLang(PUBLISHING_DETAILS_BY_COUNTRY, country, lang));
	let legalEntitiesHref = $derived(
		getTextByCountryLang(LEGAL_ENTITIES_BY_COUNTRY, country, lang)
	);

	const updateLocale = (lang: string, country: string) => {
		const countryCode = countryCodeLookup.get(country);
		const newLocale = countryCode ? `${lang.substring(0, 2)}-${countryCode}` : lang;
		$localeStore = getValidLocale(newLocale);
	};
</script>

<footer class="Footer" class:Footer--centered={centered} data-theme="dark">
	<section class="Footer__info">
		<h2 class="Footer__heading">{$t('Footer.contactPersons')}</h2>
		<div class="Footer__info-block">
			<address data-testid="Footer_Address" class="Footer__address">{address}</address>
			<ul class="Footer__info-cell">
				<li>
					<a class="Footer__link" href="mailto:info@swisslife-am.com"
						>info@swisslife-am.com</a
					>
				</li>
				<li>
					<a class="Footer__link" href={`tel:+${phone.replace(/[^0-9]/g, '')}`}>{phone}</a
					>
				</li>
			</ul>
		</div>
		<div class="Footer__info-block">
			<ul data-testid="Footer_LegalInfo" class="Footer__info-cell">
				<li>
					<a class="Footer__link" href={`${PAGES.termsAndConditions}/${invType}`}
						>{$t('Footer.termsAndConditions')}</a
					>
				</li>
				<li>
					<a class="Footer__link" href={PAGES.termsOfUse}>{$t('Footer.terms')}</a>
				</li>
				<li>
					<a class="Footer__link" href={dataProtectionHref} target="_blank"
						>{$t('Footer.dataProtection')}</a
					>
				</li>
				<li>
					<a class="Footer__link" href={detailsHref} target="_blank"
						>{$t('Footer.details')}</a
					>
				</li>
				<li>
					<a class="Footer__link" href={legalEntitiesHref} target="_blank"
						>{$t('Footer.policies')}</a
					>
				</li>
			</ul>
			<ul data-testid="Footer_ContactInfo" class="Footer__info-cell">
				<li>
					<a class="Footer__link" href={$t('Footer.locationsHref')} target="_blank"
						>{$t('Footer.locations')}</a
					>
				</li>
				<li>
					<a class="Footer__link" href={contactsHref} target="_blank"
						>{$t('Footer.contactPersons')}</a
					>
				</li>
			</ul>
		</div>
	</section>
	<div class="Footer__actions">
		<ul class="Footer__social-icons">
			{#each socialIcons as { icon, href, label } (label)}
				{@const SvelteComponent = icon}
				<li class="Footer__social-icon">
					<a
						class="Footer__social-icon-link"
						{href}
						target="_blank"
						aria-label={$t('Footer.socialLinkLabel', { values: { portalName: label } })}
					>
						<SvelteComponent></SvelteComponent>
					</a>
				</li>
			{/each}
		</ul>
		<div class="Footer__theme-switch">
			{#key currentTheme}
				<Switch
					testid="Footer_ThemeSwitcher"
					variant="theme"
					label="dark"
					labelChecked="light"
					onChange={() => setTheme(currentTheme === 'light' ? 'dark' : 'light', true)}
					checked={currentTheme === 'light'}
				></Switch>
			{/key}
		</div>
		<div class="Footer__combobox">
			<Select
				testid="Footer_LanguageSelect"
				variant="button-soft-centered"
				label={$t('Footer.language')}
				placement="top"
				onChange={(detail) => {
					updateLocale(detail ?? '', country);
				}}
				items={langItems}
				defaultSelection={langItems.find(
					(item) => item.value === $localeStore.substring(0, 2)
				)}
				theme="dark"
			></Select>
		</div>
	</div>
</footer>

<style src="./Footer.scss" lang="scss"></style>
